import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Heart, Copy, Share2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Public mini-fic card for a companion thread.
 *
 * Shows the companion's portrait, trope, and three "standout" replies pulled
 * from the thread. Fully public (no auth) so share links posted to Twitter /
 * Reddit / Discord render properly and funnel traffic back to ProseCraft.
 */
export default function ThreadReplay() {
  const { threadId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/share/thread-replay/${threadId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Thread not found"))
      .finally(() => setLoading(false));
  }, [threadId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy — long-press the link to share instead");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe grid place-items-center text-slate-300">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen-safe grid place-items-center text-center px-6">
        <div>
          <div className="font-serif text-xl text-slate-200">That thread isn't available</div>
          <p className="mt-2 text-sm text-slate-500">The link may have expired or been removed.</p>
          <Link to="/companions" className="inline-block mt-5 rounded-full bg-amber-500 hover:bg-amber-400 text-black px-5 py-2 text-sm font-semibold">
            Meet the cast →
          </Link>
        </div>
      </div>
    );
  }

  const { companion, replies } = data;
  const portrait = companion.portrait_url ? fileUrl(companion.portrait_url) : null;
  const alignmentChip =
    companion.alignment === "bad"
      ? { label: "Villain", cls: "border-rose-500/60 text-rose-200" }
      : companion.alignment === "good"
      ? { label: "Hero", cls: "border-emerald-500/60 text-emerald-200" }
      : { label: "Anti-hero", cls: "border-amber-500/60 text-amber-200" };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="thread-replay-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-3 flex items-center gap-2">
        <Share2 className="h-3 w-3" /> A ProseCraft mini-fic · shared by a reader
      </div>
      <article className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#0F1117] to-[#07080A] overflow-hidden shadow-[0_18px_60px_rgba(0,0,0,0.5)]">
        <div className="grid sm:grid-cols-[200px_1fr]">
          <div className="relative aspect-[3/4] sm:aspect-auto bg-black">
            {portrait ? (
              <img src={portrait} alt={companion.name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full grid place-items-center text-slate-700 font-mono text-xs">
                no portrait
              </div>
            )}
            <div className="absolute top-3 left-3">
              <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded border backdrop-blur bg-black/40 ${alignmentChip.cls}`}>
                {alignmentChip.label}
              </span>
            </div>
          </div>
          <div className="p-6 sm:p-7">
            <div className="font-mono text-[10px] uppercase tracking-widest text-rose-300">{companion.trope}</div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-slate-50 mt-1" data-testid="replay-companion-name">
              {companion.name}
            </h1>
            {companion.tagline && (
              <p className="text-sm text-slate-400 mt-2 italic">"{companion.tagline}"</p>
            )}
            <div className="mt-5 border-t border-white/10 pt-5 space-y-4">
              {replies.length === 0 ? (
                <div className="text-xs text-slate-500 italic">
                  This thread doesn't have any quotable moments yet.
                </div>
              ) : replies.map((r, i) => (
                <blockquote
                  key={i}
                  data-testid={`replay-reply-${i}`}
                  className="relative pl-5 border-l-2 border-amber-400/50"
                >
                  <div className="font-mono text-[9px] uppercase tracking-widest text-amber-400 mb-1">
                    {i === 0 ? "Opening" : i === replies.length - 1 ? "Latest" : "Mid-arc"}
                  </div>
                  <div className="font-serif italic text-[15px] leading-relaxed text-slate-200">
                    "{r.text}"
                  </div>
                </blockquote>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <Link
                to={`/companions/${companion.companion_id}`}
                data-testid="replay-chat-cta"
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 text-sm font-semibold"
              >
                Chat with {companion.name.split(" ")[0]} <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={copyLink}
                data-testid="replay-copy"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 hover:border-amber-400/40 text-slate-200 px-4 py-2 text-sm"
              >
                <Copy className="h-4 w-4" /> Copy share link
              </button>
            </div>
          </div>
        </div>
      </article>

      <div className="mt-10 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2 flex items-center justify-center gap-2">
          <Heart className="h-3 w-3 text-rose-400" /> Love this?
        </div>
        <p className="text-sm text-slate-400 max-w-md mx-auto">
          Pick your own trope, write your own prompt, and ProseCraft spins up a full romance chapter in seconds.
        </p>
        <Link to="/" className="inline-block mt-4 text-amber-300 hover:text-amber-200 text-sm font-mono uppercase tracking-widest">
          Explore ProseCraft →
        </Link>
      </div>
    </div>
  );
}
